/* eslint-disable import/no-cycle */
import firebase from 'firebase/compat/app';
import { makeAutoObservable, toJS } from 'mobx';
import {
  loadKameleoonFlagsAndTestsPrequal,
  getQueryParam,
  isFromBDP,
  getImtId,
  getBoatInfoRaw,
  getPrequal,
  betterParseFloat,
  generateUUID,
} from '../utils';
import {
  Prequal,
  ShoppingSegmentType,
  FullApplicationStatus,
  CreditScoreRangeType,
  InternalAttribution,
  Marketing,
  PreQualUTM,
  LeadOrigin,
  Address,
} from '../schema';
import { BOAT_TRADER_SOURCE_ID } from '../constants';
import { getNumericQueryParam } from '../utils/queryParamHelper';
import utils from '../utils/liveParams';

export class PrequalStore {
  imtId: string | null = null;

  source: number | null = null;

  subsource: number | null = null;

  leadPage: string | null = null;

  boatInfo: string | null = null;

  prequalApplicationId: string | null = null;

  leadUUID: string | null = null;

  leadOrigin: LeadOrigin = LeadOrigin.prequal;

  isBDP: boolean = false;

  prequal: Partial<Prequal> = {};

  navDirection: 1 | -1 | 'other' = 1;

  keyCode: string | null = null;

  promoCode: string | null = null;

  repCode: string | null = null;

  subSourceLabel: string | null = null;

  sourceLabel: string | null = null;

  utm: PreQualUTM = {
    utmSource: null,
    utmMedium: null,
    utmTerm: null,
    utmContent: null,
    utmCampaign: null,
    utmId: null,
    utmName: null,
  };

  // TRIDENT-896: internal-referral attribution. Captured from internal_* URL params.
  internal: InternalAttribution = {
    internalSource: null,
    internalMedium: null,
    internalCampaign: null,
    internalContent: null,
    internalTerm: null,
    internalId: null,
    internalName: null,
    internalPosition: null,
  };

  marketing: Marketing = {
    adGroup: null,
    adGroupId: null,
    campaignName: null,
    campaignId: null,
  };

  variant: string | null = null;

  kamExpVariant: string | null = null;

  constructor() {
    this.prequalApplicationId = generateUUID();
    this.isBDP = isFromBDP();

    this.boatInfo = this.isBDP ? getBoatInfoRaw() : null;
    this.imtId = this.isBDP ? getImtId() : null;

    this.marketing.adGroupId = getNumericQueryParam({ param: 'adgroupid' });
    this.marketing.campaignId = getNumericQueryParam({ param: 'campaignid' });
    this.source = getNumericQueryParam({ param: 'source' }) || BOAT_TRADER_SOURCE_ID;
    this.subsource = getNumericQueryParam({ param: 'subsource' });

    this.keyCode = getQueryParam('keycode');
    this.leadPage = getQueryParam('leadPage');
    // TRIDENT-896: capture internal-referral attribution params.
    this.internal.internalSource = getQueryParam('internal_source');
    this.internal.internalMedium = getQueryParam('internal_medium');
    this.internal.internalCampaign = getQueryParam('internal_campaign');
    this.internal.internalContent = getQueryParam('internal_content');
    this.internal.internalTerm = getQueryParam('internal_term');
    this.internal.internalId = getQueryParam('internal_id');
    this.internal.internalName = getQueryParam('internal_name');
    this.internal.internalPosition = getQueryParam('internal_position');
    this.marketing.adGroup = getQueryParam('adgroup');
    this.marketing.campaignName = getQueryParam('campaign');
    this.promoCode = getQueryParam('promocode');
    this.repCode = getQueryParam('repid');
    this.leadOrigin = LeadOrigin.prequal;
    this.utm.utmCampaign = getQueryParam('utm_campaign');
    this.utm.utmContent = getQueryParam('utm_content');
    this.utm.utmId = getQueryParam('utm_id');
    this.utm.utmMedium = getQueryParam('utm_medium');
    this.utm.utmName = getQueryParam('utm_name');
    this.utm.utmSource = getQueryParam('utm_source');
    this.utm.utmTerm = getQueryParam('utm_term');
    this.variant = getQueryParam('variant');
    this.kamExpVariant = getQueryParam('kamExpVariant');
    makeAutoObservable(this);
  }

  setSourceLabel({
    sourceLabel,
    subSourceLabel,
  }: {
    sourceLabel: string | null;
    subSourceLabel: string | null;
  }) {
    this.prequal.sourceLabel = sourceLabel;
    this.prequal.subSourceLabel = subSourceLabel;

    this.sourceLabel = sourceLabel;
    this.subSourceLabel = subSourceLabel;
  }

  setPrequal(): any {
    this.prequal = getPrequal(this);
  }

  setShoppingSegment({ shoppingSegment }: { shoppingSegment: ShoppingSegmentType }): void {
    this.prequal.shoppingSegment = shoppingSegment;
  }

  setPurchasePrice({ purchasePrice }: { purchasePrice: string }): void {
    this.prequal.purchasePrice = betterParseFloat(purchasePrice);
  }

  setMonthlyPayment({
    prequalApprovalMonthlyPayment,
  }: {
    prequalApprovalMonthlyPayment: string;
  }): void {
    this.prequal.prequalApprovalMonthlyPayment = betterParseFloat(prequalApprovalMonthlyPayment);
  }

  setDownPayment({ downPayment }: { downPayment: string }): void {
    this.prequal.downPayment = betterParseFloat(downPayment);
    if (
      this.prequal.purchasePrice !== undefined &&
      this.prequal.purchasePrice !== null &&
      this.prequal.downPayment !== undefined &&
      this.prequal.downPayment !== null
    ) {
      this.prequal.loanAmount = this.prequal.purchasePrice - this.prequal.downPayment;
    }
  }

  setSSN(ssn: string | null): void {
    this.prequal.ssn = ssn;
  }

  setFicoScore({ creditScore }: { creditScore: CreditScoreRangeType }): void {
    this.prequal.creditScore = creditScore;
    this.prequal.completedAt = Date.now();
  }

  setAboutYou(
    {
      firstName,
      lastName,
      email,
      phone,
    }: {
      firstName: string;
      lastName: string;
      email: string;
      phone: string;
    },
    address: Address | null | undefined
  ): void {
    this.prequal.firstName = firstName;
    this.prequal.lastName = lastName;
    this.prequal.email = email;
    this.prequal.phone = phone;
    this.prequal.address = address;
    this.prequal.completedAt = Date.now();
  }

  setResults(): void {
    this.prequal.fullApplicationStatus = FullApplicationStatus.started;
    this.prequal.fullApplicationStartedAt = Date.now();

    const imtId = this.prequal?.imtId || 'NONBDP';
    const storageKey = `prequal-${imtId}`;
    localStorage.setItem(storageKey, JSON.stringify(this.prequal));
  }

  /**
   * @description "Live" params are params that need to be set as soon as they are noticed, no matter where the
   * customer is at in the application funnel. If a "live" param is present, it should overwrite whatever was currently
   * stored in the loan.
   */
  getLiveParams() {
    return utils.getLiveParams();
  }

  /**
   * @description Given an object of params where { paramName: paramValue }, update the loan so that it contains
   * the newly received "live params"
   */
  setLiveParams() {
    utils.setLiveParams(this.prequal);
  }
}

let prequalStore: PrequalStore | null = null;
const w: any = window;

export function usePrequal(): PrequalStore {
  if (prequalStore) {
    prequalStore.setLiveParams();
    return prequalStore;
  }

  prequalStore = new PrequalStore();
  prequalStore.setPrequal();
  prequalStore.setLiveParams();

  loadKameleoonFlagsAndTestsPrequal(prequalStore.prequal);

  w.TF = w.TF || {};
  w.TF.prequalStore = prequalStore;
  return prequalStore;
}

export function getPrequalStore() {
  return prequalStore;
}

/**
 * for tests
 */
export function setPrequalStore(store: PrequalStore | null) {
  prequalStore = store;
}

w.toJS = toJS;
